import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedTitle } from './sourceMeta';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../ui/Dialog';
import Button from '../ui/Button';
import type { PendingActionItem } from '../../api/pendingActions';
import { requiresComment, supportsNote, completeActionLabel } from './dispatch';

interface DecisionDialogProps {
  /** The (item, decision) being confirmed; null = closed. */
  target: { item: PendingActionItem; positive: boolean } | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}

/**
 * Note-capture step for inbox decisions. Positive decisions take an optional
 * note; negative ones require it where the domain demands a reason
 * (leave / claim / asset reject).
 */
const DecisionDialog: React.FC<DecisionDialogProps> = ({ target, submitting, onClose, onConfirm }) => {
  const { t } = useTranslation('requests');
  const [note, setNote] = useState('');
  const [showRequired, setShowRequired] = useState(false);

  useEffect(() => {
    // Reset the form whenever a new decision opens.
    setNote('');
    setShowRequired(false);
  }, [target]);

  if (!target) return null;
  const { item, positive } = target;
  const noteRequired = requiresComment(item, positive);
  const noteSupported = supportsNote(item);
  // Swap acceptance can apply schedules the moment it's accepted — say so before the tap.
  const swapAcceptInfo =
    item.source === 'ShiftSwapAcceptance' && positive
      ? item.meta?.autoAppliesOnAccept === 'true'
        ? t('Accepting applies the swap to your schedule immediately.')
        : t('Accepting sends the swap to your manager for approval.')
      : null;

  const completeLabel = t(completeActionLabel(item.source));
  const titles: Record<string, string> = {
    Decision2: positive ? t('Approve request') : t('Reject request'),
    Accept2: positive ? t('Accept request') : t('Decline request'),
    Complete1: t('Confirm'),
    OpenOnly: t('Confirm'),
  };
  const confirmLabels: Record<string, string> = {
    Decision2: positive ? t('Approve') : t('Reject'),
    Accept2: positive ? t('Accept') : t('Decline'),
    Complete1: completeLabel,
    OpenOnly: t('Confirm'),
  };

  const handleConfirm = () => {
    if (noteRequired && note.trim() === '') {
      setShowRequired(true);
      return;
    }
    onConfirm(note.trim());
  };

  const dismiss = (open: boolean) => {
    // Lock every dismissal path together while the decision is in flight.
    if (!open && !submitting) onClose();
  };

  return (
    <Dialog open onOpenChange={dismiss}>
      <DialogContent size="sm" hideCloseButton={submitting}>
        <DialogHeader>
          <DialogTitle>{titles[item.actionStyle] ?? t('Confirm')}</DialogTitle>
          <DialogDescription>
            {[item.actorName, localizedTitle(item, t)].filter(Boolean).join(' · ')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {swapAcceptInfo && <p className="mb-3 text-sm text-muted-foreground">{swapAcceptInfo}</p>}
          {!noteSupported && !swapAcceptInfo && (
            <p className="text-sm text-muted-foreground">{t('This action takes effect right away.')}</p>
          )}
          {noteSupported && (
            <>
          <label htmlFor="pa-decision-note" className="mb-1.5 block text-sm font-medium text-foreground">
            {noteRequired ? t('Note (required)') : t('Note (optional)')}
          </label>
          <textarea
            id="pa-decision-note"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (showRequired && e.target.value.trim() !== '') setShowRequired(false);
            }}
            rows={3}
            maxLength={1000}
            autoFocus={noteRequired}
            disabled={submitting}
            placeholder={t('Add context for this decision')}
            data-testid="pa-dialog-note"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
          />
          {showRequired && (
            <p className="mt-1.5 text-sm text-destructive" role="alert">
              {t('A note is required')}
            </p>
          )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('Cancel')}
          </Button>
          <Button
            variant={positive ? 'success' : 'destructive'}
            onClick={handleConfirm}
            loading={submitting}
            data-testid="pa-dialog-submit"
          >
            {confirmLabels[item.actionStyle] ?? t('Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DecisionDialog;
